import React, { useState, useEffect } from 'react'
import styles from './ShopPage.module.scss'
import WelcomeHeader from '../../WelcomePage/components/WelcomeHeader/WelcomeHeader'
import { getShopProducts, getShopCategories, getShopCart, addToCart } from '../../../service/ShopService'
import ProductCard from './components/ProductCard/ProductCard'
import CategoryFilter from './components/CategoryFilter/CategoryFilter'
import ShoppingCart from './components/ShoppingCart/ShoppingCart'
import Loader from '../../../components/Loader/Loader'
import { WarningIcon } from '../../../components/CustomIcons'

const ShopPage = ({ embedded = false, goBack }) => {
  const [products, setProducts] = useState([])
  const [categories, setCategories] = useState([])
  const [cart, setCart] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [retryAction, setRetryAction] = useState(null)
  const [activeCategory, setActiveCategory] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [view, setView] = useState('products') // products | cart | product-detail
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  useEffect(() => {
    // Проверяем авторизацию
    const token = localStorage.getItem('access_token')
    setIsAuthenticated(!!token)
  }, [])

  useEffect(() => {
    loadInitialData()
  }, [])

  const loadInitialData = async () => {
    try {
      setLoading(true)
      setError(null)
      const [productsResponse, categoriesResponse, cartResponse] = await Promise.all([
        getShopProducts({ limit: 20 }),
        getShopCategories(),
        getShopCart().catch(() => ({ data: null })) // Cart may not exist for unauthenticated users
      ])
      
      setProducts(productsResponse.data.results || productsResponse.data)
      setCategories(categoriesResponse.data.results || categoriesResponse.data)
      setCart(cartResponse.data)
    } catch (err) {
      setError('Ошибка загрузки данных магазина')
      setRetryAction(() => loadInitialData)
      console.error('Shop data loading error:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleCategoryChange = async (categoryId) => {
    try {
      setActiveCategory(categoryId)
      setLoading(true)
      setError(null)
      const params = categoryId ? { category: categoryId } : {}
      const response = await getShopProducts(params)
      setProducts(response.data.results || response.data)
    } catch (err) {
      setError('Ошибка загрузки товаров категории')
      setRetryAction(() => () => handleCategoryChange(categoryId))
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = async (query) => {
    try {
      setSearchQuery(query)
      setLoading(true)
      setError(null)
      const params = { search: query }
      if (activeCategory) params.category = activeCategory
      const response = await getShopProducts(params)
      setProducts(response.data.results || response.data)
    } catch (err) {
      setError('Ошибка поиска товаров')
      setRetryAction(() => () => handleSearch(query))
    } finally {
      setLoading(false)
    }
  }

  const handleAddToCart = async (productId, quantity = 1) => {
    try {
      setError(null)
      await addToCart(productId, quantity)
      // Reload cart
      const cartResponse = await getShopCart()
      setCart(cartResponse.data)
    } catch (err) {
      setError('Ошибка добавления товара в корзину')
      setRetryAction(() => () => handleAddToCart(productId, quantity))
    }
  }

  const handleRetry = () => {
    if (retryAction) {
      retryAction()
    }
  }

  const handleProductClick = (product) => {
    setSelectedProduct(product)
    setView('product-detail')
  }

  const handleBackToProducts = () => {
    setView('products')
    setSelectedProduct(null)
  }

  if (loading && products.length === 0) {
    return (
      <div className={styles.page}>
        {!embedded && <WelcomeHeader />}
        <div className={styles.loader}>
          <Loader />
        </div>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      {!embedded && <WelcomeHeader />}
      
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>
            {view === 'products' && 'Интернет-магазин'}
            {view === 'cart' && 'Корзина'}
            {view === 'product-detail' && selectedProduct?.name}
          </h1>
          
          <div className={styles.headerControls}>
            {view !== 'products' && (
              <button 
                onClick={view === 'cart' ? () => setView('products') : handleBackToProducts}
                className={styles.backButton}
              >
                ← Назад
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className={styles.errorContainer}>
            <div className={styles.errorIcon}><WarningIcon size={48} /></div>
            <div className={styles.errorContent}>
              <h3 className={styles.errorTitle}>Произошла ошибка</h3>
              <p className={styles.errorMessage}>{error}</p>
              <button 
                onClick={handleRetry}
                className={styles.retryButton}
                disabled={loading}
              >
                {loading ? 'Загрузка...' : 'Повторить'}
              </button>
            </div>
          </div>
        )}

        {view === 'products' && (
          <>
            <div className={styles.controls}>
              <div className={styles.searchContainer}>
                <input
                  type="text"
                  placeholder="Поиск товаров..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  className={styles.searchInput}
                />
              </div>
              
              <CategoryFilter
                categories={categories}
                activeCategory={activeCategory}
                onCategoryChange={handleCategoryChange}
              />
            </div>

            <div className={styles.productsGrid}>
              {products.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onAddToCart={handleAddToCart}
                  onClick={handleProductClick}
                  isAuthenticated={isAuthenticated}
                />
              ))}
            </div>

            {products.length === 0 && !loading && (
              <div className={styles.emptyState}>
                <h3>Товары не найдены</h3>
                <p>Попробуйте изменить параметры поиска</p>
              </div>
            )}
          </>
        )}

        {view === 'cart' && (
          <ShoppingCart 
            cart={cart} 
            onUpdateCart={setCart}
            onBackToShopping={() => setView('products')}
            isAuthenticated={isAuthenticated}
          />
        )}

        {view === 'product-detail' && selectedProduct && (
          <div className={styles.productDetail}>
            <div className={styles.productDetailContent}>
              <div className={styles.productImages}>
                <img 
                  src={selectedProduct.main_image || selectedProduct.image || '/placeholder-product.jpg'} 
                  alt={selectedProduct.name}
                  className={styles.productImage}
                />
              </div>
              
              <div className={styles.productInfo}>
                <h2 className={styles.productName}>{selectedProduct.name}</h2>
                <div className={styles.productPrice}>{selectedProduct.price} Сом</div>
                
                <div className={styles.productDescription}>
                  <h4>Описание</h4>
                  <p>{selectedProduct.description || 'Описание товара недоступно'}</p>
                </div>

                <div className={styles.productActions}>
                  <button
                    onClick={() => handleAddToCart(selectedProduct.id)}
                    className={styles.addToCartButton}
                  >
                    Добавить в корзину
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {loading && (
          <div className={styles.loading}>
            <Loader />
          </div>
        )}
      </div>

      {/* Плавающая кнопка корзины */}
      {cart && cart.items && cart.items.length > 0 && (
        <button
          className={styles.floatingCartBtn}
          onClick={() => setView('cart')}
        >
          <svg className={styles.cartIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 3H5.5L5.6 3.5M7 13H17L21 5H6.5M7 13L5.6 3.5M7 13L5.5 15.5M17 13L18.5 15.5M7.5 18.5C7.5 19.0523 7.05228 19.5 6.5 19.5C5.94772 19.5 5.5 19.0523 5.5 18.5C5.5 17.9477 5.94772 17.5 6.5 17.5C7.05228 17.5 7.5 17.9477 7.5 18.5ZM19 18.5C19 19.0523 18.5523 19.5 18 19.5C17.4477 19.5 17 19.0523 17 18.5C17 17.9477 17.4477 17.5 18 17.5C18.5523 17.5 19 17.9477 19 18.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span className={styles.cartCount}>{cart.items.length}</span>
          <span className={styles.cartTotal}>
            {cart.items.reduce((sum, item) => sum + (item.product?.price || 0) * item.quantity, 0).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} сом
          </span>
        </button>
      )}
    </div>
  )
}

export default ShopPage